// One field's change, committed on its own tick.
//
// Inertia cancels an in-flight visit when a new one starts, so two quick
// ticks would leave the first field with no idea whether it landed. Every
// inline save is therefore queued behind the last one: they are single-field
// PATCHes, so the wait is a few milliseconds and the receipt is always real.
import { router } from "@inertiajs/react"

let queue: Promise<unknown> = Promise.resolve()

function firstMessage(errors: Record<string, string>) {
  const messages = Object.values(errors).filter(Boolean)
  return messages[0] ?? "Could not save that change."
}

/** Resolves once the server has taken the change; rejects with the first
 *  validation error so the field can show it in place. */
export function inlinePatch(
  url: string,
  data: Record<string, unknown>,
  options: { forceFormData?: boolean } = {},
): Promise<void> {
  const run = () =>
    new Promise<void>((resolve, reject) => {
      router.patch(
        url,
        { ...data, inline: true },
        {
          preserveScroll: true,
          preserveState: true,
          // A cover photo goes up as a file, which needs a multipart body.
          forceFormData: options.forceFormData,
          onSuccess: () => resolve(),
          onError: (errors) => reject(new Error(firstMessage(errors as Record<string, string>))),
          onCancel: () => reject(new Error("Save interrupted — try again.")),
        },
      )
    })

  const next = queue.then(run, run)
  // The queue itself must never reject, or one failed field would block the
  // next one from ever being sent.
  queue = next.catch(() => {})
  return next
}
